/*
 * Copyright (c) 2016 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.visflux.chord

import com.macrofocus.common.properties.PropertiesListener
import com.macrofocus.common.properties.PropertyEvent
import org.mkui.visual.Visual
import org.molap.network.NetworkListener
import org.molap.network.WeightedNetwork

/**
 * The arcs of a chord diagram of a network: each node gets an arc proportional to the sum of the weights of its links
 * (outgoing links when the network is directed, all of them otherwise), separated by a padding of a fifth of the
 * circle in total; the arc of a node is divided between its links.
 */
class DefaultChordModel<Node, Link, Column>(
    val network: ChordNetwork<Node, Link>,
    override val nodesVisual: Visual<Node, Column>?,
    override val linksVisual: Visual<Link, Column>?,
) : AbstractChordModel<Node, Link, Column>() {
    constructor(network: WeightedNetwork<Node, Link, Any>, nodesVisual: Visual<Node, Column>?, linksVisual: Visual<Link, Column>?) :
        this(WeightedChordNetwork(network), nodesVisual, linksVisual)

    override var arcPadding = 0.5
        private set

    private var nodeChord: MutableMap<Node, Arc> = HashMap()
    private var linkChord: MutableMap<Node, MutableMap<Node, Arc>> = HashMap()

    private val networkListener = object : NetworkListener {
        override fun networkChanged() {
            update()
        }
    }

    init {
        network.addNetworkListener(networkListener)
        properties.addPropertiesListener(object : PropertiesListener<ChordModel.PropertyType> {
            override fun propertyChanged(name: ChordModel.PropertyType, event: PropertyEvent<Any?>) {
                update()
            }
        })
        buildArcs()
        buildLinksArcs()
    }

    private fun update() {
        buildArcs()
        buildLinksArcs()
        notifyChordChanged()
    }

    /** Stops following the network. */
    fun dispose() {
        network.removeNetworkListener(networkListener)
    }

    private val isDirected: Boolean
        get() = network.isDirected

    private fun buildArcs() {
        val nodeChord = HashMap<Node, Arc>()
        val nodeCount = network.nodes.count()
        arcPadding = 360.0 * 0.2 / nodeCount
        val usableAngle = 360.0 - nodeCount * arcPadding
        val sizes = HashMap<Node, Double>().apply { for (node in network.nodes) put(node, getNodeSize(node)) }
        val totalNodesSize = sizes.values.sum()
        var currentAngle = 0.0
        for (node in network.nodes) {
            val nodeSize = sizes[node] ?: 0.0
            val angle = if (nodeSize > 0) nodeSize * usableAngle / totalNodesSize else 0.0
            nodeChord[node] = Arc(currentAngle, angle)
            currentAngle += angle + arcPadding
        }
        this.nodeChord = nodeChord
    }

    private fun buildLinksArcs() {
        val linkChord = HashMap<Node, MutableMap<Node, Arc>>()
        for (from in network.nodes) {
            val nodeSize = getNodeSize(from)
            val nodeArc = nodeChord[from] ?: continue
            var currentAngle = nodeArc.startAngle
            val usableAngle = nodeArc.extent
            var links: List<Link> = (if (isDirected) network.getEdgesFrom(from) else network.getEdges(from)).toList()
            if (minimizedCrossing.value) {
                val angle = nodeArc.startAngle + nodeArc.extent / 2
                links = links.sortedWith { o1, o2 ->
                    val chord1 = getArc(getTo(o1))
                    val chord2 = getArc(getTo(o2))
                    if (chord1 == null || chord2 == null) {
                        when {
                            chord1 == null && chord2 == null -> 0
                            chord1 == null -> -1
                            else -> 1
                        }
                    } else {
                        crossingOrder(chord1, angle).compareTo(crossingOrder(chord2, angle))
                    }
                }
            }
            for (link in links) {
                val extent = if (nodeSize > 0) getLinkSize(link) * usableAngle / nodeSize else 0.0
                // The other end of the link: its destination, or its origin for a link entering the node (undirected)
                val other = if (network.getFrom(link) == from) network.getTo(link) else network.getFrom(link)
                linkChord.getOrPut(from) { HashMap() }[other] = Arc(currentAngle, extent)
                currentAngle += extent
            }
        }
        this.linkChord = linkChord
    }

    /** The order of the destination of a link around the circle seen from the middle of the origin, from the opposite side. */
    private fun crossingOrder(chord: Arc, angle: Double): Double {
        var c = chord.startAngle + chord.extent / 2 - angle
        if (c > 180) {
            c -= 360
        } else if (c < -180) {
            c += 360
        }
        return if (c >= 0) 180 - c else -180 - c
    }

    override fun getNodeSize(node: Node): Double {
        var size = 0.0
        val links = if (isDirected) network.getEdgesFrom(node) else network.getEdges(node)
        for (link in links) {
            size += getLinkSize(link)
        }
        return size
    }

    override fun getLinkSize(link: Link): Double {
        var size = 0.0
        val value = network.getWeight(link)
        if (value is Number) {
            val v = value.toDouble()
            if (!v.isNaN()) size += v
        }
        if (!isDirected) {
            val reverse = network.getWeight(network.getReverse(link))
            if (reverse is Number) {
                val v = reverse.toDouble()
                if (!v.isNaN()) size += v
            }
        }
        return size
    }

    override val nodes: Iterable<Node>
        get() = network.nodes

    override fun getLinks(node: Node): Iterable<Link> = network.getEdgesFrom(node)

    override fun getArc(node: Node): Arc? = nodeChord[node]

    override fun getArc(from: Node, to: Node): Arc? = linkChord[from]?.get(to)

    override fun getFrom(link: Link): Node = network.getFrom(link)

    override fun getTo(link: Link): Node = network.getTo(link)
}
